import { usePathname, type UnknownOutputParams } from 'expo-router'
import { useEffect, useRef } from 'react'

import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import type { TGTMVariables } from '@/src/general/utils/gtm/types/GTMVariables'

/** Matches the intention page and its pray/reward sub-pages: /intention/:id-:shareToken(/…) */
const INTENTION_PAGE_REGEX = /^\/intention\/(\d+-[^/]+)/

/** Pages the reward "Continue" button can go through before getting back to the intention page. */
const POST_PRAY_SEGMENTS = new Set(['watchman', 'rosario'])

export const usePageViewAnalyticsTracking = (searchParams: UnknownOutputParams) => {
  // Intention visited last, kept during the post-pray flow so that coming back to it is not a new page view
  const trackedIntentionVisit = useRef<string | undefined>(undefined)
  const pathname = usePathname()
  const formattedPathname = pathname.split('/')[1]
  const ids = Object.entries(searchParams).reduce<string>(
    (acc, [key, value]) =>
      key.toLowerCase().includes('id') ? `${acc}-${Array.isArray(value) ? value.join(',') : value}` : acc,
    '',
  )
  const intentionIdSharetoken = pathname.match(INTENTION_PAGE_REGEX)?.[1]
  // The intention page, its prayer and its reward are one single visit
  const pageKey = intentionIdSharetoken ? `intention-${intentionIdSharetoken}` : `${formattedPathname}${ids}`

  useEffect(() => {
    const variables: TGTMVariables = {}

    if (intentionIdSharetoken) {
      if (trackedIntentionVisit.current === intentionIdSharetoken) return
      trackedIntentionVisit.current = intentionIdSharetoken
      variables.pageTitle = 'Intention'
    } else if (!POST_PRAY_SEGMENTS.has(formattedPathname)) {
      trackedIntentionVisit.current = undefined
    }

    GTM.sendEvent({ event: EVENTS.PAGEVIEW }, variables)
  }, [pageKey, intentionIdSharetoken, formattedPathname])
}
